import { useState, useEffect } from "react";
import RightTimeApi from "@/API"; // Ensure you import RightTimeApi if it's used
import { FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { MdCheckBoxOutlineBlank, MdCheckBox, MdCameraswitch } from "react-icons/md";
import { CiEdit } from "react-icons/ci";
import { toast } from "react-toastify";
import { RiSave3Fill } from "react-icons/ri";
import { ImCancelCircle } from "react-icons/im";
import { apiMessage } from "@/utils/apiMessage";

export default function TestStatuses(props) {
  const [editTestStatus, updateEditTestStatus] = useState(null);
  const [testStatuses, updateTestStatuses] = useState(null);
  const [isBillableHover, setIsBillableHover] = useState(false);
  const [isArchiveHover, setIsArchiveHover] = useState(false);

  useEffect(() => {
    updateEditTestStatus(null);
  },[props.activeTestType]);

  useEffect(() => {
    updateTestStatuses(props.testStatuses || []);
  }, [props.testStatuses]);

  if(!props.testStatuses)
    return;

  async function onEditTestStatusAsync(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    let ts = testStatuses.find((ts) => ts.id === id);
    if (ts) {
      ts.applicable = props.activeTestType.statuses.includes(ts.id);
      updateEditTestStatus(ts);
    }
  }
  async function onAddStatusAsync() {
    const newStatus =  { id: 0, code: "", description: "", display: '', applicable: true };
    updateEditTestStatus(newStatus);
    updateTestStatuses([...testStatuses, newStatus]);
  }

  async function updateStatusAsync(e)
  {
    let element = e.currentTarget.getAttribute("data-element");
    updateEditTestStatus({
      ...editTestStatus,
      [element]: e.target.value,
    });
  }

  async function SaveTestStatusAsync(e) {
    editTestStatus.CompanyTestTypeId = props.activeTestType.companyTestType.id;
    editTestStatus.TestTypeId = props.activeTestType.companyTestType.testTypeId;
    editTestStatus.CompanyId = props.activeTestType.companyTestType.companyId;
    const resp = await RightTimeApi.sendAsync(
      "/CompanyTestStatus",
      editTestStatus
    );
    if (resp && resp.ok) {
      updateEditTestStatus({ id: null });
      toast.success("Test status updated.");
      props.updateData();
    }
  }

  async function resetEditModeAsync() {
    updateEditTestStatus({ id: null });
  }

  async function onCancelEditStatusAsync(e) {
    resetEditModeAsync();
  }

  async function deleteStatusAsync(e) {
    let id = parseInt(e.currentTarget.getAttribute("data-id"));
    const resp = await RightTimeApi.deleteAsync("/CompanyTestStatus/" + id);
    props.updateData();
  }

  async function onToggleTestStatusAsync() {
    updateEditTestStatus({
      ...editTestStatus,
      applicable: !editTestStatus.applicable,
    });
  }

  async function toggleVisibleAsync(e) {
    let statusId = parseInt(e.currentTarget.getAttribute("data-statusId"), 10);
    if (props.activeTestType && statusId > 0) {
      const resp = await RightTimeApi.putAsync(
        "/CompanyTestType/"+props.activeTestType.companyTestType.id+"/StatusVisibleToggle/"+statusId
      );
      if (resp && resp.ok) {
        toast.success(apiMessage(resp));
        props.updateData();
      }
    }
  }

  async function toggleStatusAsync(e) {
    let statusId = parseInt(e.currentTarget.getAttribute("data-statusId"), 10);
    if (props.activeTestType && statusId > 0) {
      let statusData = {
        companyId: props.activeTestType.companyTestType.companyId,
        testTypeId: props.activeTestType.companyTestType.testTypeId,
        companyTestTypeId: props.activeTestType.companyTestType.id,
        statusId: statusId,
      };
      const resp = await RightTimeApi.putAsync(
        "/ToggleTestTypeStatus",
        statusData
      );
      if (resp && resp.ok) {
        toast.success(apiMessage(resp));
        props.updateData();
      }
    }
  }

  async function toggleAllApplicableAsync() {
    if (!props.activeTestType || !Array.isArray(testStatuses) || testStatuses.length === 0) return;

    const applicableSet = new Set(props.activeTestType.statuses || []);
    const checkedCount = testStatuses.filter((ts) => applicableSet.has(ts.id)).length;
    const shouldEnableAll = checkedCount <= testStatuses.length / 2;

    const statusesToToggle = testStatuses.filter((ts) =>
      shouldEnableAll ? !applicableSet.has(ts.id) : applicableSet.has(ts.id)
    );

    if (statusesToToggle.length === 0) return;

    const statusDataBase = {
      companyId: props.activeTestType.companyTestType.companyId,
      testTypeId: props.activeTestType.companyTestType.testTypeId,
      companyTestTypeId: props.activeTestType.companyTestType.id
    };

    const results = await Promise.all(
      statusesToToggle.map((ts) =>
        RightTimeApi.putAsync("/ToggleTestTypeStatus", {
          ...statusDataBase,
          statusId: ts.id
        })
      )
    );

    if (results.some((resp) => resp && resp.ok)) {
      toast.success(
        shouldEnableAll
          ? "All statuses marked applicable."
          : "All statuses marked not applicable."
      );
      props.updateData();
    } else {
      toast.error("Error updating applicable statuses");
    }
  }

  async function toggleBillableAsync(e) {
    let statusId = parseInt(e.currentTarget.getAttribute("data-statusId"), 10);
    if (props.activeTestType && statusId > 0) {
      let statusData = {
        companyId: props.activeTestType.companyTestType.companyId,
        testTypeId: props.activeTestType.companyTestType.testTypeId,
        companyTestTypeId: props.activeTestType.companyTestType.id,
        statusId: statusId,
      };
      // Note: this DOES use testTypeId, not companyTestTypeId as the billable flag is on the testType
      const resp = await RightTimeApi.putAsync(
        `/Company/${statusData.companyId}/Status/${statusData.statusId}/ToggleBillable`,
        statusData
      );
      if (resp && resp.ok) {
        toast.success("Billable status updated.");
        props.updateData();
      }
    }
  }

  async function toggleAllBillableAsync() {
    if (!props.activeTestType || !Array.isArray(testStatuses) || testStatuses.length === 0) return;

    const billableSet = new Set(props.activeTestType.billableStatuses || []);
    const checkedCount = testStatuses.filter((ts) => billableSet.has(ts.id)).length;
    const uncheckedCount = testStatuses.length - checkedCount;
    const shouldDisableAll = checkedCount > uncheckedCount;

    const statusesToToggle = testStatuses.filter((ts) =>
      shouldDisableAll ? billableSet.has(ts.id) : !billableSet.has(ts.id)
    );

    if (statusesToToggle.length === 0) return;

    const statusDataBase = {
      companyId: props.activeTestType.companyTestType.companyId,
      testTypeId: props.activeTestType.companyTestType.testTypeId,
      companyTestTypeId: props.activeTestType.companyTestType.id,
    };

    const results = await Promise.all(
      statusesToToggle.map((ts) =>
        RightTimeApi.putAsync(
          `/Company/${statusDataBase.companyId}/Status/${ts.id}/ToggleBillable`,
          {
            ...statusDataBase,
            statusId: ts.id,
          }
        )
      )
    );

    if (results.some((resp) => resp && resp.ok)) {
      toast.success(
        shouldDisableAll
          ? "All statuses marked not billable."
          : "All statuses marked billable."
      );
      props.updateData();
    } else {
      toast.error("Error updating billable statuses");
    }
  }

  async function toggleArchiveAsync(e) {
    let statusId = parseInt(e.currentTarget.getAttribute("data-statusId"), 10);
    if (props.activeTestType && statusId > 0) {
      let statusData = {
        companyId: props.activeTestType.companyTestType.companyId,
        testTypeId: props.activeTestType.companyTestType.testTypeId,
        companyTestTypeId: props.activeTestType.companyTestType.id,
        statusId: statusId,
      };
      const resp = await RightTimeApi.putAsync(
        `/Company/${statusData.companyId}/Status/${statusData.statusId}/ToggleArchive`,
        statusData
      );
      if (resp && resp.ok) {
        toast.success("Archive status updated.");
        props.updateData();
      }
    }
  }

  async function toggleAllArchiveAsync() {
    if (!props.activeTestType || !Array.isArray(testStatuses) || testStatuses.length === 0) return;

    const archiveSet = new Set(props.activeTestType.archiveStatuses || []);
    const checkedCount = testStatuses.filter((ts) => archiveSet.has(ts.id)).length;
    const uncheckedCount = testStatuses.length - checkedCount;
    const shouldDisableAll = checkedCount > uncheckedCount;

    const statusesToToggle = testStatuses.filter((ts) =>
      shouldDisableAll ? archiveSet.has(ts.id) : !archiveSet.has(ts.id)
    );

    if (statusesToToggle.length === 0) return;

    const statusDataBase = {
      companyId: props.activeTestType.companyTestType.companyId,
      testTypeId: props.activeTestType.companyTestType.testTypeId,
      companyTestTypeId: props.activeTestType.companyTestType.id,
    };

    const results = await Promise.all(
      statusesToToggle.map((ts) =>
        RightTimeApi.putAsync(
          `/Company/${statusDataBase.companyId}/Status/${ts.id}/ToggleArchive`,
          {
            ...statusDataBase,
            statusId: ts.id,
          }
        )
      )
    );

    if (results.some((resp) => resp && resp.ok)) {
      toast.success(
        shouldDisableAll
          ? "All statuses marked not archive."
          : "All statuses marked archive."
      );
      props.updateData();
    } else {
      toast.error("Error updating archive statuses");
    }
  }

  return (
    <div className="tableWrapper factTable testTypeStatusesTable scrollable">
    <table className="testStatus formTable">
      <caption>
        <span className="title">TestType Statuses</span>
        <span className="captionRight">
          <FaPlusSquare
            className="actionIcon plusIcon"
            onClick={onAddStatusAsync}
            data-help-id="addTestTypeStatus"
            title="Create Status specific to this test type"
          />
        </span>
      </caption>
      <thead>
        <tr>
          <th>Name</th>
          <th>Description</th>
          <th>Display</th>
          <th>Count</th>
          <th
            className="toggleColumnHeader"
            onClick={toggleAllApplicableAsync}
              data-help-id="toggleActive"
            style={{ cursor: props.activeTestType ? "pointer" : "default" }}
            title={props.activeTestType ? "Toggle all applicable" : undefined}
          >
            <span className="toggleHeaderLabel">Applicable</span>
            <span className="toggleHeaderIcon" aria-hidden="true">
              <MdCameraswitch />
            </span>
          </th>
          <th className="toggleColumnHeader"
            onClick={toggleAllBillableAsync}
            data-help-id="toggleActive"
            onMouseEnter={() => setIsBillableHover(true)}
            onMouseLeave={() => setIsBillableHover(false)}
            style={{ cursor: props.activeTestType ? "pointer" : "default" }}
            title={props.activeTestType ? "Toggle all billable" : undefined}
          >
            {isBillableHover ? <MdCameraswitch /> : "Billable"}
          </th>
          <th className="toggleColumnHeader"
            onClick={toggleAllArchiveAsync}
            data-help-id="toggleArchive"
            onMouseEnter={() => setIsArchiveHover(true)}
            onMouseLeave={() => setIsArchiveHover(false)}
            style={{ cursor: props.activeTestType ? "pointer" : "default" }}
            title={props.activeTestType ? "Toggle all archive" : undefined}
          >
            {isArchiveHover ? <MdCameraswitch /> : "Archive"}
          </th>
          {/* <th>Customer<br/>Visible</th> */}
          <th className="centered"></th>
        </tr>
      </thead>
      <tbody>

        {/* {(()=>{debugger})()} */}

        {testStatuses &&
          testStatuses?.map((ts) => {
            let found =
              props.activeTestType &&
              props.activeTestType.statuses &&
              props.activeTestType.statuses.includes(ts.id);
            let billable = 
              props.activeTestType &&
              props.activeTestType.billableStatuses &&
              props.activeTestType.billableStatuses.includes(ts.id);
            let archive =
              props.activeTestType &&
              props.activeTestType.archiveStatuses &&
              props.activeTestType.archiveStatuses.includes(ts.id);

            return editTestStatus && editTestStatus.id === ts.id ? (
              <tr key={"r" + ts.id}>
                <td>
                  <input
                    type="text"
                    value={editTestStatus.code}
                    placeholder="Test Status Name"
                    className="inlineEdit statusNameEdit"
                    style={{ width: "30px" }}
                    data-element="code"
                    onChange={updateStatusAsync}
                  ></input>
                </td>
                <td>
                  <input
                    type="text"
                    value={editTestStatus.description}
                    placeholder="Test Status Description"
                    className="inlineEdit statusDescriptionEdit"
                    data-element="description"
                    style={{ width: "150px" }}
                    onChange={updateStatusAsync}
                  ></input>
                </td>
                <td>
                  <input
                    type="text"
                    value={editTestStatus.display}
                    placeholder="Test Status Display"
                    className="inlineEdit statusDisplayEdit"
                    style={{ width: "30px" }}
                    data-element="display"
                    onChange={updateStatusAsync}
                  ></input>
                </td>
                <td></td>
                <td className="center" onClick={onToggleTestStatusAsync}>
                {editTestStatus.applicable ? (
                    <MdCheckBox className="checkbox checked actionIcon" />
                  ) : (
                    <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon" />
                  )}
                </td>
                <td className="center" >
                </td>
                <td className="center" >
                </td>
                <td className="actions">
                  <RiSave3Fill
                    className="save actionIcon"
                    onClick={SaveTestStatusAsync}
                    title="Add"
                  />
                  <ImCancelCircle
                    className="cancel actionIcon"
                    onClick={onCancelEditStatusAsync}
                    title="Cancel"
                  />
                </td>
              </tr>
            ) : (
              <tr key={"r" + ts.id}>
                <td className="center">{ts.code}</td>
                <td>{ts.description}</td>
                <td className="center">{ts.display}</td>
                <td className="center">{ts.testCount}</td>
                <td
                  className="center"
                  onClick={toggleStatusAsync}
                  data-statusid={ts.id}>
                  {found ? (
                    <MdCheckBox className="checkbox checked actionIcon applicableCheck" />
                  ) : (
                    <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon applicableCheck" />
                  )}
                </td>
                  <td
                    className="center"
                    onClick={toggleBillableAsync}
                    data-statusid={ts.id}>
                    {billable ? (
                      <MdCheckBox className="checkbox checked actionIcon billableCheck" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon billableCheck" />
                    )}
                  </td>
                  <td
                    className="center"
                    onClick={toggleArchiveAsync}
                    data-statusid={ts.id}>
                    {archive ? (
                      <MdCheckBox className="checkbox checked actionIcon archiveCheck" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon archiveCheck" />
                    )}
                  </td>
                  {/* <td
                    className="center"
                    onClick={toggleVisibleAsync}
                    data-statusid={ts.id}>
                    {ts.visibleToCustomer ? (
                      <MdCheckBox className="checkbox checked actionIcon readOnly" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="checkbox unchecked actionIcon readOnly" />
                    )}
                  </td>                 */}
                  <td className="actions">
                  <CiEdit
                    className="actionIcon editIcon"
                    data-id={ts.id}
                    title="Edit Test Status"
                    onClick={onEditTestStatusAsync}
                  />

                  {ts.testCount === 0 ? (
                    <IoTrashOutline
                      className="actionIcon deleteIcon"
                      data-id={ts.id}
                      onClick={deleteStatusAsync}
                      title="Delete Test Status"
                    />
                  ) : (
                    <IoTrashOutline
                      className="actionIcon deleteIcon disabled"
                      title="Test instances exist with this status. Deletion not available."
                    />
                  )}
                </td>
              </tr>
            );
          })}
      </tbody>
    </table>
    </div>
  );
}

